import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';
import { TextField } from '../ui/TextField';
import { TextArea } from '../ui/TextArea';
import { FeatherX } from '../ui/icons';
import { holidayApi } from '../../services/holidayService';
import './HolidayForm.css';

const HolidayForm = ({ holiday, year, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    year: year || new Date().getFullYear(),
    is_restricted: false,
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (holiday) {
      // Format date for input (YYYY-MM-DD)
      const date = new Date(holiday.date);
      const formattedDate = date.toISOString().split('T')[0];
      
      setFormData({
        name: holiday.name || '',
        date: formattedDate,
        year: holiday.year || year || new Date().getFullYear(),
        is_restricted: holiday.is_restricted || false,
        description: holiday.description || ''
      });
    } else {
      // Set default year
      setFormData(prev => ({
        ...prev,
        year: year || new Date().getFullYear()
      }));
    }
  }, [holiday, year]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Ensure year matches the date year
      const dateYear = new Date(formData.date).getFullYear();
      const submitData = {
        ...formData,
        year: dateYear
      };

      if (holiday) {
        await holidayApi.update(holiday.id, submitData);
      } else {
        await holidayApi.create(submitData);
      }

      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save holiday');
      console.error('Error saving holiday:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return createPortal(
    <div className="holiday-form-overlay" onClick={onCancel}>
      <div className="holiday-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="holiday-form-header">
          <h2>{holiday ? 'Edit Holiday' : 'Create Holiday'}</h2>
          <button className="holiday-form-close" onClick={onCancel}>
            <FeatherX />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="holiday-form-content">
          {error && (
            <div className="holiday-form-error">
              {error}
            </div>
          )}

          <TextField label="Holiday Name *">
            <TextField.Input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., New Year's Day"
              required
            />
          </TextField>

          <TextField label="Date *">
            <TextField.Input
              type="date"
              value={formData.date}
              onChange={(e) => handleChange('date', e.target.value)}
              required
            />
          </TextField>

          <div className="holiday-form-checkbox">
            <label>
              <input
                type="checkbox"
                checked={formData.is_restricted}
                onChange={(e) => handleChange('is_restricted', e.target.checked)}
              />
              <span>Mark as restricted holiday</span>
            </label>
          </div>

          <TextArea label="Description">
            <TextArea.Input
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Optional description"
              rows="3"
            />
          </TextArea>

          <div className="holiday-form-actions">
            <Button
              type="button"
              variant="neutral-tertiary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (holiday ? 'Update' : 'Create')}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default HolidayForm;

